import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router";
import CircularProgress from "@mui/material/CircularProgress";
import Loader from "./app/components/Loader";
import Loader2 from "./app/components/Loader2";
import Box from "@mui/material/Box";
import { createBrowserHistory } from "history";
import { unstable_HistoryRouter as HistoryRouter } from "react-router-dom";
import Register from "./app/auth/Register";
import Assessment from "./app/components/Assessment";
import MyCourses from "./app/components/MyCourses";
import SkillBuilder from "./app/components/SkillBuilder";
import ProjectDashboard from "./app/components/ProjectDashboard";
import AIDemo from "./app/components/AIDemoV1";
import AISkillJourney from "./app/components/AiSkillJourney/AISkillJourney";
import AIAssessments from "./app/components/AIAssessments";
import AIDemoV4 from "./app/components/AIDemoV4";
import AiAssessmentDashboard from "./app/components/AiAssessmentDashboard";
import TakeAssessment from "./app/components/TakeAssessment";
import UploadedFiles from "./app/components/UploadedFiles";
import GeneratedAssessments from "./app/components/GeneratedAssessments";
import AIOwnJourney from "./app/components/AiSelfJourney/AISelfJourney";

const Competition = lazy(() => import("./app/components/Competition"));
const CompetitionDetails = lazy(() => import("./app/components/CompetitionDetails"));
const CompetitionLeaderboard = lazy(() => import("./app/components/CompetitionLeaderboard"));

// import EditCv from './app/components/EditCv';
const Login = lazy(() => import("./app/auth/Login"));
const Logout = lazy(() => import("./app/auth/Logout"));

const CreateAccount = lazy(() => import("./app/auth/CreateAccount"));
const ForgotPassword = lazy(() => import("./app/auth/ForgotPassword"));
const Success = lazy(() => import("./app/auth/Success"));
const Errors = lazy(() => import("./app/auth/Errors"));
const Expired = lazy(() => import("./app/auth/Expired"));
const ParentalConsent = lazy(() => import("./app/auth/ParentalConsent"));
const SuccessParental = lazy(() => import("./app/auth/SuccessParental"));
const EmailVerification = lazy(() => import("./app/auth/EmailVerification"));
const EmailActivation = lazy(() => import("./app/auth/EmailActivation"));
const RegistrationCompletion = lazy(() =>
  import("./app/auth/RegistrationCompletion")
);

const Profile = lazy(() => import("./app/components/Profile"));

const Portfolio = lazy(() => import("./app/components/Portfolio"));
const CreateProject = lazy(() => import("./app/components/CreateProject"));
const EditProject = lazy(() => import("./app/components/EditProject"));
const CreateProjectPreview = lazy(() =>
  import("./app/components/CreateProjectPreview")
);

const CreateCV = lazy(() => import("./app/components/CreateCV"));
const ViewCV = lazy(() => import("./app/components/ViewCV"));
const CreateCVDetails = lazy(() => import("./app/components/CreateCVDetails"));
const EditCv = lazy(() => import("./app/components/EditCv"));
const ViewCVNew = lazy(() => import("./app/components/ViewCVNew"));

const MyStudents = lazy(() => import("./app/components/MyStudents"));
const QuizResults = lazy(() => import("./app/components/QuizResults"));
const QuizResultsDetails = lazy(() =>
  import("./app/components/QuizResultsDetails")
);

const MyConnections = lazy(() => import("./app/components/MyConnections"));
const ConnectionRequests = lazy(() =>
  import("./app/components/MyConnections/ConnectionRequests")
);
const AllConnections = lazy(() =>
  import("./app/components/MyConnections/AllConnections")
);
const FindConnections = lazy(() =>
  import("./app/components/MyConnections/FindConnections")
);
const InviteConnections = lazy(() =>
  import("./app/components/MyConnections/InviteConnections")
);
const MutualConnections = lazy(() =>
  import("./app/components/MyConnections/MutualConnections")
);

const MyFeed = lazy(() => import("./app/components/MyFeed"));
const MyPost = lazy(() => import("./app/components/Post/MyPost"));
const CategoryList = lazy(() => import("./app/components/Post/CategoryList"));
const MyPostDetails = lazy(() => import("./app/components/Post/MyPostDetails"));
const MyDiaryDetails = lazy(() =>
  import("./app/components/Post/MyDiaryDetails")
);
const MyDiary = lazy(() => import("./app/components/Post/MyDiary"));

const MyStuff = lazy(() => import("./app/components/MyStuff"));
const ProjectGuide = lazy(() =>
  import("./app/components/MyStuff/ProjectGuide")
);
const TeamMember = lazy(() => import("./app/components/MyStuff/TeamMember"));

const Setting = lazy(() => import("./app/components/Setting"));
const AccountInformation = lazy(() =>
  import("./app/components/Settings/AccountInformation")
);
const ChangePassword = lazy(() =>
  import("./app/components/Settings/ChangePassword")
);
const ContactDetails = lazy(() =>
  import("./app/components/Settings/ContactDetails")
);
const ParentalConsentSettings = lazy(() =>
  import("./app/components/Settings/ParentalConsent")
);
const Interests = lazy(() => import("./app/components/Settings/Interests"));

const AdminView = lazy(() => import("./app/admin/Index"));
const ClipAdminView = lazy(() => import("./app/clipAdmin/Index"));

const Scripts = lazy(() => import("./app/components/Scripts"));

const GroupAdminDashboard = lazy(() =>
  import("./app/components/GroupSchoolDashboard")
);
const SchoolAdminDashboard = lazy(() =>
  import("./app/components/SchoolAdminDashboard")
);

const TeacherAssessment = lazy(()=> import("./app/components/TeacherAssessment"))

const AIDemoV3 = lazy(() => import("./app/components/AIDemoV3"));
const OrganizationAdminDashboard = lazy(() =>
  import("./app/components/OrganizationAdminComponents/OrganizationDashboard")
);
const GroupOrganizationAdminDashboard = lazy(() =>
  import("./app/components/OrganizationAdminComponents/GroupOrganizationDashboard")
);
const CohortDashboard = lazy(() =>
  import("./app/components/OrganizationAdminComponents/CohortDashboard")
);

const AiAssessmentDashboardForInstructor = lazy(() =>
  import("./app/components/AiAssessmentDashboardForInstructor")
);

const OrganizationAiAssessmentDashboard = lazy(() =>
  import("./app/components/OrganizationAdminComponents/AiAssessmentDashboard")
);

const AiAssessmentForCohort = lazy(() =>
  import("./app/components/OrganizationAdminComponents/AIAssessmentsForCohort")
);

const DirectAssessmentForCohort = lazy(() =>
  import("./app/components/OrganizationAdminComponents/DirectAssessmentForCohort")
);

const PublishBooks = lazy(() =>
  import("./app/components/PublishBooks")
);

const StudentSelfLearning = lazy(() =>
  import("./app/components/StudentSelfLearning/StudentSelfLearning")
);

let history = createBrowserHistory();

const router = (
  <Suspense
    fallback={
      <Box className="appLoader">
        <Loader2 />
      </Box>
    }
  >
    <Routes history={history}>
      <Route path="login" element={<Navigate to="/signin" />} />
      <Route path={`${process.env.PUBLIC_URL}/*`} element={<Login />} />
      <Route path={`${process.env.PUBLIC_URL}/signin`} element={<Login />} />
      <Route
        path={`${process.env.PUBLIC_URL}/register`}
        element={<Register />}
      />
      <Route path={`${process.env.PUBLIC_URL}/logout`} element={<Logout />} />
      <Route
        path={`${process.env.PUBLIC_URL}/create-account`}
        element={<CreateAccount />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/forgot-password`}
        element={<ForgotPassword />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/success/:type`}
        element={<Success />}
      />
      <Route path={`${process.env.PUBLIC_URL}/errors`} element={<Errors />} />
      <Route path={`${process.env.PUBLIC_URL}/expired`} element={<Expired />} />
      <Route
        path={`${process.env.PUBLIC_URL}/parental-consent`}
        element={<ParentalConsent />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/success-parental/:id/:email`}
        element={<SuccessParental />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/email-verification/:encodeemail/:encodeotp`}
        element={<EmailVerification />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/email-activation/:encodeemail`}
        element={<EmailActivation />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/registration-completion/:encodeuserid`}
        element={<RegistrationCompletion />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/profile/:id`}
        element={<Profile />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/portfolio`}
        element={<Portfolio />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/competitions`}
        element={<Competition />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/competitions/:competitionId`}
        element={<CompetitionDetails />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/competitions/:competitionId/leaderboard`}
        element={<CompetitionLeaderboard />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/create-project`}
        element={<CreateProject />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/edit-project/:id`}
        element={<EditProject />}
      />
      <Route
        key={"project"}
        path={`${process.env.PUBLIC_URL}/project/:username/:id`}
        element={<CreateProjectPreview />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/create-cv`}
        element={<CreateCV />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/cv/:username/:id`}
        element={<ViewCV />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/create-cv-details`}
        element={<CreateCVDetails />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/edit-cv/:id`}
        element={<EditCv />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/view-cv-new`}
        element={<ViewCVNew />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/my-students`}
        element={<MyStudents />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/project-dashboard`}
        element={<ProjectDashboard />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/group-admin`}
        element={<GroupAdminDashboard />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/school-admin/:schoolid`}
        element={<SchoolAdminDashboard />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/teacher/assessment`}
        element={<TeacherAssessment />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/quiz-results`}
        element={<QuizResults />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/quiz-result-details`}
        element={<QuizResultsDetails />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/assessment/:id`}
        element={<Assessment />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/my-courses`}
        element={<MyCourses />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/my-connections`}
        element={<MyConnections />}
      >
        <Route
          index={true}
          path={`connection-requests`}
          element={<ConnectionRequests />}
        />
        <Route
          index={false}
          path={`all-connections`}
          element={<AllConnections />}
        />
        <Route
          index={false}
          path={`find-connections`}
          element={<FindConnections />}
        />
        <Route
          index={false}
          path={`invite-connections`}
          element={<InviteConnections />}
        />
        <Route
          index={false}
          path={`mutual-connections/:id`}
          element={<MutualConnections />}
        />
      </Route>

      <Route path={`${process.env.PUBLIC_URL}/skill-builder`} element={<SkillBuilder />} />
      <Route path={`${process.env.PUBLIC_URL}/ai-demo-v3`} element={<AIDemoV3 />} />
      <Route path={`${process.env.PUBLIC_URL}/my-feed`} element={<MyFeed />} />
      <Route
        path={`${process.env.PUBLIC_URL}/my-diary`}
        element={<MyDiary />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/blogs/:id`}
        element={<MyPost />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/blog/:username/:id`}
        element={<MyPostDetails />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/diary/:username/:id`}
        element={<MyDiaryDetails />}
      />
      <Route
        path={`${process.env.PUBLIC_URL}/posts/:cat/:slug`}
        element={<CategoryList />}
      />

      <Route path={`${process.env.PUBLIC_URL}/my-stuff`} element={<MyStuff />}>
        <Route index={true} path={`project-member`} element={<TeamMember />} />
        <Route
          index={false}
          path={`project-guide`}
          element={<ProjectGuide />}
        />
      </Route>

      <Route path={`${process.env.PUBLIC_URL}/settings`} element={<Setting />}>
        <Route
          index={true}
          path={`account-information`}
          element={<AccountInformation />}
        />
        <Route
          index={false}
          path={`change-password`}
          element={<ChangePassword />}
        />
        <Route
          index={false}
          path={`contact-details`}
          element={<ContactDetails />}
        />
        <Route
          index={false}
          path={`parental-consent`}
          element={<ParentalConsentSettings />}
        />
        <Route index={false} path={`interests`} element={<Interests />} />
      </Route>

      <Route
        path={`${process.env.PUBLIC_URL}/ai-demo`}
        element={<AIDemo />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/ai-assessment-dashboard`}
        element={<AiAssessmentDashboard />}
      />  

      <Route
        path={`${process.env.PUBLIC_URL}/uploaded-files`}
        element={<UploadedFiles />}
      />  

      <Route
        path={`${process.env.PUBLIC_URL}/generated-assessments`}
        element={<GeneratedAssessments />}
      />  

      <Route
        path={`${process.env.PUBLIC_URL}/take-assessment`}
        element={<TakeAssessment />}
      />  

      <Route
        path={`${process.env.PUBLIC_URL}/ai-skill-journey/:id`}
        element={<AISkillJourney />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/ai-assessments/:id`}
        element={<AIAssessments />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/ai-skill-journey`}
        element={<AISkillJourney />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/ai-self-learning-journey`}
        element={<AIOwnJourney />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/ai-self-learning-journey/:id`}
        element={<AIOwnJourney />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/ai-demo-v4`}
        element={<AIDemoV4 />}
      />

       <Route 
        path={`${process.env.PUBLIC_URL}/group-organization-dashboard`}
        element={<GroupOrganizationAdminDashboard />}
      />

      <Route 
        path={`${process.env.PUBLIC_URL}/organization-dashboard/:id`}
        element={<OrganizationAdminDashboard />}
      />

      <Route 
        path={`${process.env.PUBLIC_URL}/organization-dashboard`}
        element={<OrganizationAdminDashboard />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/cohort-dashboard/:id`}
        element={<CohortDashboard />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/cohort-dashboard`}
        element={<CohortDashboard />}
      />
      
      <Route
        path={`${process.env.PUBLIC_URL}/organization-ai-assessment-dashboard`}
        element={<OrganizationAiAssessmentDashboard />}
      />
      
      <Route
        path={`${process.env.PUBLIC_URL}/cohort-ai-assessment-dashboard`}
        element={<AiAssessmentDashboardForInstructor />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/ai-assessment-for-cohort/:cohortId/:contentId`}
        element={<AiAssessmentForCohort />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/direct-assessment-for-cohort/:cohortId/:contentId`}
        element={<DirectAssessmentForCohort />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/publisher/books`}
        element={<PublishBooks />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/student-self-learning`}
        element={<StudentSelfLearning />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/admin1003/*`}
        element={<AdminView />}
      />

      <Route
        path={`${process.env.PUBLIC_URL}/clipadmin1003/*`}
        element={<ClipAdminView />}
      />

      <Route path={`${process.env.PUBLIC_URL}/scripts`} element={<Scripts />} />
      
    </Routes>
  </Suspense>
);

export default router;
